import React, { useState, useEffect, useRef, useCallback } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { api, API_BASE } from '../utils/api';
import { Send, Calendar, AlertCircle, Info, Loader2, CheckCircle, Paperclip, List, Globe } from 'lucide-react';
import { formatDays } from '../utils/helpers'; 

// --- [START] ส่วนของคำแปล (Translations Object) ---
const translations = {
  th: {
    langToggle: "EN",
    title: "ยื่นใบลา",
    subtitle: "กรอกแบบฟอร์มเพื่อส่งคำขออนุมัติการลา และตรวจสอบโควต้าคงเหลือ",
    balanceTitleTimes: "จำนวนครั้ง (รวม)",
    balanceTitleDays: "จำนวนวันลา (รวม)",
    balanceLoading: "กำลังโหลดโควต้า...",
    balanceUnitTimes: "ครั้ง",
    balanceUnitDays: "วัน",
    balanceRemaining: "คงเหลือ",
    balanceUsed: "ใช้ไป",
    balanceOf: "/",
    balanceUnlimited: "ไม่จำกัด",
    formTitle: "กรอกรายละเอียดการลา",
    leaveTypeLabel: "ประเภทการลา",
    leaveTypePlaceholder: "-- กรุณาเลือก --",
    leaveTypeSick: "ลาป่วย",
    leaveTypePersonal: "ลากิจ",
    leaveTypeMaternity: "ลาคลอด",
    startDateLabel: "ตั้งแต่วันที่",
    endDateLabel: "ถึงวันที่",
    totalDaysPrefix: "รวมจำนวนวันลา (หักวันหยุด/เสาร์-อาทิตย์, รวมวันทำงานพิเศษ):",
    totalDaysSuffix: "วัน",
    reasonLabel: "เหตุผลการลา",
    reasonPlaceholder: "กรุณาระบุเหตุผลประกอบการลา...",
    attachmentSickLabel: "แนบใบรับรองแพทย์", 
    attachmentPersonalLabel: "แนบใบสอนแทน", 
    attachmentRequiredSick: "(จำเป็นสำหรับ 3 วันขึ้นไป) *", 
    attachmentRequiredPersonal: "(จำเป็นต้องแนบทุกครั้ง) *", 
    attachmentButton: "เลือกไฟล์รูปภาพ",
    attachmentRemoving: "ลบ",
    signatureLabel: "ลงลายมือชื่อ (กรุณาลงลายมือชื่อบริเวณตรงกลาง)",
    signatureClear: "ล้างลายเซ็น",
    submitButtonText: "ยื่นใบลา (รอพิจารณา)",
    submitButtonSigning: "กรุณาลงลายมือชื่อก่อนส่ง",
    submitButtonSubmitting: "กำลังส่งข้อมูล...",
    listTitle: "ประวัติการลาของฉัน",
    listLoading: "กำลังโหลดประวัติ...",
    listError: "ไม่สามารถดึงประวัติการลาได้:",
    listEmpty: "ไม่พบประวัติการลา",
    listHeaderType: "ประเภท",
    listHeaderDate: "วันที่ลา",
    listHeaderDays: "จำนวนวัน",
    listHeaderStatus: "สถานะ",
    listHeaderManage: "จัดการ",
    listStatusPending: "รอพิจารณา",
    listStatusApproved: "อนุมัติแล้ว",
    listStatusRejected: "ไม่อนุมัติ",
    listAttachmentTooltip: "ดู/พิมพ์ เอกสารแนบ", 
    errorSignature: "กรุณาลงลายมือชื่อผู้ยื่นลา",
    errorFormIncomplete: "กรุณากรอกข้อมูลให้ครบถ้วน: ประเภทการลา, วันที่ลา (อย่างน้อย 1 วัน), และเหตุผล",
    errorAttachmentRequired: (type) => { 
      if (type === 'ลาป่วย') return "กรุณาแนบใบรับรองแพทย์ (เนื่องจากลาป่วย 3 วันขึ้นไป)";
      if (type === 'ลากิจ') return "กรุณาแนบใบสอนแทน (เนื่องจากเป็นการลากิจ)";
      return "กรุณาแนบไฟล์เอกสารประกอบ";
    }, 
    errorQuotaExceeded: "ไม่สามารถยื่นใบลาได้ เนื่องจากเกินโควต้าที่กำหนด",
    errorDateInvalid: "วันที่สิ้นสุดต้องไม่ก่อนวันที่เริ่มต้น",
    errorImageOnly: "กรุณาเลือกไฟล์รูปภาพเท่านั้น (เช่น .jpg, .png)",
    errorImageProcess: "ไม่สามารถประมวลผลรูปภาพได้",
    errorFetchBalance: "ไม่สามารถโหลดข้อมูลโควต้าการลาได้",
    errorSubmit: "ไม่สามารถยื่นใบลาได้",
    errorConnection: "เกิดข้อผิดพลาดในการเชื่อมต่อ:",
    infoCompressing: "กำลังบีบอัดรูปภาพ...",
    infoMaternity: "การลาคลอดจะถูกนับเฉพาะจำนวนครั้ง ไม่นับรวมในโควต้าจำนวนวัน",
    successSubmit: "ยื่นใบลาสำเร็จ เรื่องของท่านจะถูกส่งให้ผู้ดูแลระบบพิจารณา",
    warningQuotaDays: (used, current, total, max) => `แจ้งเตือน: คุณกำลังจะใช้โควต้าวันลาใกล้หมด (ใช้ไป ${total} จาก ${max} วัน)`,
    warningQuotaTimes: (used, current, total, max) => `แจ้งเตือน: คุณกำลังจะใช้โควต้าจำนวนครั้งใกล้หมด (ใช้ไป ${total} จาก ${max} ครั้ง)`,
    errorQuotaDays: (used, current, total, max) => `ลาเกินโควต้า! คุณลาไปแล้ว ${used} วัน และกำลังจะลาอีก ${current} วัน (รวม ${total} วัน) ซึ่งเกินโควต้าสูงสุด ${max} วัน`,
    errorQuotaTimes: (used, current, total, max) => `ลาเกินโควต้า! คุณลาไปแล้ว ${used} ครั้ง และกำลังจะลาอีก 1 ครั้ง (รวม ${total} ครั้ง) ซึ่งเกินโควต้าสูงสุด ${max} ครั้ง`,
    printAttachmentTitle: "เอกสารแนบใบลา",
    printAttachmentAlt: "เอกสารแนบ",
    printError: "เกิดข้อผิดพลาด:",
    printErrorShow: "เกิดข้อผิดพลาดในการแสดงไฟล์แนบ:",
    printPopupBlocker: "ไม่สามารถเปิดหน้าต่างสำหรับพิมพ์ได้ กรุณาตรวจสอบการตั้งค่า Pop-up Blocker",
  },
  en: {
    langToggle: "TH",
    title: "Leave Request",
    subtitle: "Fill out the form to submit a leave request and check your remaining quotas.",
    balanceTitleTimes: "Leave Taken (Times)",
    balanceTitleDays: "Leave Taken (Days)",
    balanceLoading: "Loading quotas...",
    balanceUnitTimes: "times",
    balanceUnitDays: "days",
    balanceRemaining: "Remaining",
    balanceUsed: "Used",
    balanceOf: "of",
    balanceUnlimited: "Unlimited",
    formTitle: "Leave Request Details",
    leaveTypeLabel: "Leave Type",
    leaveTypePlaceholder: "-- Please Select --",
    leaveTypeSick: "Sick Leave",
    leaveTypePersonal: "Personal Leave",
    leaveTypeMaternity: "Maternity Leave",
    startDateLabel: "Start Date",
    endDateLabel: "End Date",
    totalDaysPrefix: "Total leave days (adjusted for weekends/holidays):",
    totalDaysSuffix: "Days",
    reasonLabel: "Reason for Leave",
    reasonPlaceholder: "Please provide a reason for your leave...",
    attachmentSickLabel: "Attach Medical Certificate", 
    attachmentPersonalLabel: "Attach Substitution Form", 
    attachmentRequiredSick: "(Required for 3+ days) *", 
    attachmentRequiredPersonal: "(Required every time) *", 
    attachmentButton: "Choose Image File",
    attachmentRemoving: "Remove",
    signatureLabel: "Signature (Please sign in the center)",
    signatureClear: "Clear Signature",
    submitButtonText: "Submit Request (Pending)",
    submitButtonSigning: "Please Sign Before Submitting",
    submitButtonSubmitting: "Submitting...",
    listTitle: "My Leave History",
    listLoading: "Loading history...",
    listError: "Could not fetch leave history:",
    listEmpty: "No leave history found.",
    listHeaderType: "Type",
    listHeaderDate: "Date",
    listHeaderDays: "Days",
    listHeaderStatus: "Status",
    listHeaderManage: "Actions",
    listStatusPending: "Pending",
    listStatusApproved: "Approved",
    listStatusRejected: "Rejected",
    listAttachmentTooltip: "View/Print Attachment", 
    errorSignature: "Please provide your signature.",
    errorFormIncomplete: "Please complete all required fields: Leave Type, Dates (at least 1 day), and Reason.",
    errorAttachmentRequired: (type) => { 
      if (type === 'ลาป่วย') return "A medical certificate is required for sick leave of 3 days or more.";
      if (type === 'ลากิจ') return "A substitution form is required for personal leave.";
      return "Please attach a supporting document.";
    },
    errorQuotaExceeded: "Cannot submit request: You have exceeded your leave quota.",
    errorDateInvalid: "End date cannot be before start date.",
    errorImageOnly: "Please select an image file only (e.g., .jpg, .png).",
    errorImageProcess: "Could not process the image.",
    errorFetchBalance: "Could not load leave balance data.",
    errorSubmit: "Failed to submit leave request.",
    errorConnection: "Connection error:",
    infoCompressing: "Compressing image...",
    infoMaternity: "Maternity leave only counts towards the number of times, not the day quota.",
    successSubmit: "Leave request submitted successfully! Your request will be reviewed.",
    warningQuotaDays: (used, current, total, max) => `Warning: You are approaching your day quota (Using ${total} of ${max} days).`,
    warningQuotaTimes: (used, current, total, max) => `Warning: You are approaching your time quota (Using ${total} of ${max} times).`,
    errorQuotaDays: (used, current, total, max) => `Quota Exceeded! You have already used ${used} days and are requesting ${current} more (Total ${total} days), which exceeds the maximum of ${max} days.`,
    errorQuotaTimes: (used, current, total, max) => `Quota Exceeded! You have already used ${used} times and are requesting 1 more (Total ${total} times), which exceeds the maximum of ${max} times.`,
    printAttachmentTitle: "Leave Attachment",
    printAttachmentAlt: "Attachment",
    printError: "An error occurred:",
    printErrorShow: "Error displaying attachment:",
    printPopupBlocker: "Could not open print window. Please check your pop-up blocker settings.",
  }
};
// --- [END] ส่วนของคำแปล ---

// --- [START] ฟังก์ชัน Helper สำหรับการพิมพ์ (Print Logic) ---
const formatDateThai = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const options = {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'Asia/Bangkok',
  };
  return date.toLocaleDateString('th-TH', options);
};

const formatDateEng = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const options = {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  };
  return date.toLocaleDateString('en-US', options);
};

const handlePrintAttachment = (attachmentDataUrl, t) => {
  let printWindow;
  try {
    let imageUrl;
    if (attachmentDataUrl.startsWith('data:image')) {
      imageUrl = attachmentDataUrl;
    } else {
      imageUrl = `${API_BASE}${attachmentDataUrl}`;
    }

    const printContent = `
      <!DOCTYPE html>
      <html lang="${t.lang}">
      <head>
        <meta charset="UTF-8">
        <title>${t.printAttachmentTitle}</title>
        <style>
          @font-face { font-family: 'Sarabun'; src: url('${API_BASE}fonts/THSarabun.ttf') format('truetype'); }
          @page { size: A4 portrait; margin: 1.5cm; }
          body { 
            margin: 0; 
            padding: 0; 
            font-family: 'Sarabun', sans-serif; 
            word-break: keep-all;
            line-height: 1;
          }
          .container { width: 100%; text-align: center; }
          img { max-width: 100%; max-height: 95vh; height: auto; width: auto; object-fit: contain; }
          @media print { body { margin: 0; } }
        </style>
      </head>
      <body>
        <div class="container">
          <img src="${imageUrl}" alt="${t.printAttachmentAlt}" /> </div>
      </body>
      </html>
    `;

    printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert(t.printPopupBlocker);
      return;
    }
    printWindow.document.write(printContent);
    printWindow.document.close();

    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 500); 

  } catch (err) {
    console.error("Error printing attachment:", err);
    const errorMsg = t.printErrorShow + err.message;
    if (printWindow) {
      printWindow.document.write(`<h3 style="font-family: Sarabun, sans-serif; color: red;">${errorMsg}</h3>`);
    } else {
      alert(errorMsg);
    }
  }
};

const getStatusBadge = (status) => {
  switch (status) {
    case 'pending':
      return 'bg-yellow-100 text-yellow-800';
    case 'approved':
      return 'bg-green-100 text-green-800';
    case 'rejected':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};
// --- [END] ฟังก์ชัน Helper ---

const LeaveBalanceSummary = ({ 
  title, used, max, unit, loading,
  loadingText, remainingText, usedText, ofText, unlimitedText 
}) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center p-4 bg-gray-100 rounded-lg animate-pulse">
        <Loader2 className="h-5 w-5 mr-2 animate-spin text-gray-400" />
        <span className="text-sm text-gray-500">{loadingText}</span>
      </div>
    );
  }

  const isUnlimited = (max === 0 || max === null);
  const formattedUsed = formatDays(used);
  const formattedMax = formatDays(max);
  const formattedRemaining = isUnlimited ? unlimitedText : formatDays(max - used);

  const percentage = isUnlimited ? 0 : (used / max) * 100;

  return (
    <div className="p-6 bg-white rounded-lg shadow">
      <div className="text-sm font-semibold text-gray-700 mb-2">{title}</div>
      <div className="flex items-center justify-between">
        {isUnlimited ? (
          <>
            <span className="text-lg font-bold text-gray-800">
              {formattedUsed} <span className="text-sm font-normal text-gray-600">{unit}</span>
            </span>
            <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
              {unlimitedText}
            </span>
          </>
        ) : (
          <>
            <span className="text-lg font-bold text-gray-800">
              {formattedRemaining} <span className="text-sm font-normal text-gray-600">{remainingText}</span>
            </span>
            <span className="text-sm text-gray-600">
              ({usedText} {formattedUsed} {ofText} {formattedMax} {unit})
            </span>
          </>
        )}
      </div>
      {!isUnlimited && (
        <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
          <div
            className={`h-2.5 rounded-full ${percentage > 90 ? 'bg-red-600' : percentage > 70 ? 'bg-yellow-500' : 'bg-blue-600'}`}
            style={{ width: `${percentage}%` }}
          ></div>
        </div>
      )}
    </div>
  );
};

const compressImage = (file, maxWidth = 800, quality = 0.7) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        let width = img.width;
        let height = img.height;

        if (width > maxWidth) {
          height = (maxWidth / width) * height;
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        
        const dataUrl = canvas.toDataURL(file.type, quality); 
        resolve(dataUrl);
      };
      img.onerror = (error) => reject(error);
    };
    reader.onerror = (error) => reject(error);
  });
};

/**
 * คอมโพเนนต์ฟอร์มยื่นใบลา
 */
const LeaveRequestForm = ({ currentUser }) => {
  const [lang, setLang] = useState('th');
  const t = translations[lang]; 

  const [leaveType, setLeaveType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [totalDays, setTotalDays] = useState(0);
  const [reason, setReason] = useState('');

  const sigPad = useRef(null);
  const [signatureEmpty, setSignatureEmpty] = useState(true);

  const [attachment, setAttachment] = useState(null);
  const [attachmentName, setAttachmentName] = useState('');
  const fileInputRef = useRef(null);

  const [leaveBalance, setLeaveBalance] = useState(null); 
  const [loadingBalance, setLoadingBalance] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [alertMessage, setAlertMessage] = useState({ type: '', text: '' });

  const [myRequests, setMyRequests] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState('');

  const [holidays, setHolidays] = useState([]);
  const [extraWorkDays, setExtraWorkDays] = useState([]);

  useEffect(() => {
    const fetchCalendarData = async () => {
      try {
        const currentYear = new Date().getFullYear();
        const [
          thisYearHolidays, nextYearHolidays,
          thisYearExtra, nextYearExtra
        ] = await Promise.all([
          api.getHolidays(currentYear),
          api.getHolidays(currentYear + 1),
          api.getExtraWorkDays(currentYear),
          api.getExtraWorkDays(currentYear + 1)
        ]);

        setHolidays([...(thisYearHolidays || []), ...(nextYearHolidays || [])]);
        setExtraWorkDays([...(thisYearExtra || []), ...(nextYearExtra || [])]);

      } catch (error) {
        console.error("Failed to fetch calendar data:", error);
      }
    };
    fetchCalendarData();
  }, []);

  useEffect(() => {
    if (!currentUser?.employee_id) return;
    const fetchBalance = async () => {
      setLoadingBalance(true);
      try {
        const balanceData = await api.getLeaveBalance(currentUser.employee_id);
        setLeaveBalance(balanceData);
      } catch (error) {
        console.error("Error fetching leave balance:", error);
        setMessage({ type: 'error', text: t.errorFetchBalance });
      } finally {
        setLoadingBalance(false);
      }
    };
    fetchBalance();
  }, [currentUser, t.errorFetchBalance]); 

  useEffect(() => {
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);

      if (end < start) {
        setTotalDays(0);
        setAlertMessage({ type: 'error', text: t.errorDateInvalid });
        return;
      }

      let days = 0;
      const current = new Date(start.getTime());
      
      const holidaySet = new Set(holidays.map(h => h.holiday_date));
      const extraWorkDaySet = new Set(extraWorkDays.map(xw => xw.work_date));

      while (current <= end) {
        const dayOfWeek = current.getDay();
        const dateStr = current.toISOString().split('T')[0];

        const isWeekend = (dayOfWeek === 0 || dayOfWeek === 6);
        const isHoliday = holidaySet.has(dateStr);
        const isExtraWorkDay = extraWorkDaySet.has(dateStr);

        if (!isHoliday && (!isWeekend || isExtraWorkDay)) {
          days++;
        }
        
        current.setDate(current.getDate() + 1);
      }
      
      setTotalDays(days);
      if (alertMessage.text.includes(t.errorDateInvalid)) {
        setAlertMessage({ type: '', text: '' });
      }
      
    } else {
      setTotalDays(0);
    }
  }, [startDate, endDate, holidays, extraWorkDays, t.errorDateInvalid, alertMessage.text]); 

  useEffect(() => {
    if (!leaveType || totalDays <= 0 || !leaveBalance) {
      if (!alertMessage.text.includes(t.errorDateInvalid)) {
         setAlertMessage({ type: '', text: '' });
      }
      return;
    }

    const usedData = leaveBalance.used['combined_leave'];
    const quotaData = leaveBalance.current_quotas;
    
    const maxDays = quotaData.leave_days;
    const maxTimes = quotaData.leave_times;
    
    const usedDays = usedData.used_days ? parseFloat(usedData.used_days) : 0;
    const usedTimes = usedData.used_times ? parseInt(usedData.used_times) : 0;
    
    const shouldCountDays = (leaveType !== 'ลาคลอด'); 
    
    const newTotalDays = usedDays + (shouldCountDays ? totalDays : 0);
    const newTotalTimes = usedTimes + 1;

    let warningText = '';
    let errorText = '';

    const formattedUsedDays = formatDays(usedDays);
    const formattedTotalDays = formatDays(totalDays);
    const formattedNewTotalDays = formatDays(newTotalDays);
    const formattedMaxDays = formatDays(maxDays);

    if (shouldCountDays && maxDays > 0) {
      if (newTotalDays > maxDays) {
        errorText = t.errorQuotaDays(formattedUsedDays, formattedTotalDays, formattedNewTotalDays, formattedMaxDays);
      } else if (newTotalDays > maxDays * 0.9) {
        warningText += t.warningQuotaDays(formattedUsedDays, formattedTotalDays, formattedNewTotalDays, formattedMaxDays) + ' ';
      }
    }

    if (maxTimes > 0) {
      if (newTotalTimes > maxTimes) {
        errorText = t.errorQuotaTimes(usedTimes, 1, newTotalTimes, maxTimes);
      } else if (newTotalTimes > maxTimes * 0.9) {
        warningText += t.warningQuotaTimes(usedTimes, 1, newTotalTimes, maxTimes);
      }
    }

    if (errorText) {
      setAlertMessage({ type: 'error', text: errorText });
    } else if (warningText) {
      setAlertMessage({ type: 'warning', text: warningText });
    } else if (leaveType === 'ลาคลอด') {
      setAlertMessage({ type: 'info', text: t.infoMaternity });
    } else {
      setAlertMessage({ type: '', text: '' });
    }

  }, [leaveType, totalDays, leaveBalance, t, alertMessage.text]);
  
  useEffect(() => {
    if (leaveType !== 'ลาป่วย' && leaveType !== 'ลากิจ') {
      setAttachment(null);
      setAttachmentName('');
    }
  }, [leaveType]);

  const fetchMyRequests = useCallback(async () => {
    if (!currentUser || !currentUser.employee_id) return;
    setLoadingList(true);
    setListError('');
    try {
      const data = await api.getLeaveRequests(currentUser.employee_id);
      setMyRequests(data);
    } catch (err) {
      console.error("Error fetching my leave requests:", err);
      setListError(t.listError + err.message);
    } finally {
      setLoadingList(false);
    }
  }, [currentUser, t.listError]); 

  useEffect(() => {
    if (currentUser?.employee_id) {
      fetchMyRequests();
    }
  }, [currentUser, fetchMyRequests]);

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: t.errorImageOnly });
      return;
    }

    setSubmitting(true);
    setMessage({ type: 'info', text: t.infoCompressing });

    try {
      const compressedBase64 = await compressImage(file, 800, 0.7);
      
      setAttachment(compressedBase64);
      setAttachmentName(file.name);
      setMessage({ type: '', text: '' });

    } catch (error) {
      console.error("Error compressing image:", error);
      setMessage({ type: 'error', text: t.errorImageProcess });
    } finally {
      setSubmitting(false);
      if (fileInputRef.current) fileInputRef.current.value = null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (sigPad.current.isEmpty()) {
      setSignatureEmpty(true);
      setMessage({ type: 'error', text: t.errorSignature });
      return;
    }
    setSignatureEmpty(false);

    if (!leaveType || totalDays <= 0 || !reason.trim()) {
      setMessage({ type: 'error', text: t.errorFormIncomplete });
      return;
    }
    
    if ((leaveType === 'ลาป่วย' && totalDays >= 3 && !attachment) || 
        (leaveType === 'ลากิจ' && !attachment)
    ) {
      setMessage({ type: 'error', text: t.errorAttachmentRequired(leaveType) });
      return;
    }

    if (alertMessage.type === 'error') {
      setMessage({ type: 'error', text: t.errorQuotaExceeded });
      return;
    }

    setSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      const signatureDataUrl = sigPad.current.toDataURL('image/png');

      const leaveData = {
        employee_id: currentUser.employee_id,
        leave_type: leaveType,
        start_date: startDate,
        end_date: endDate,
        total_days: totalDays,
        reason: reason,
        applicant_signature: signatureDataUrl,
        attachment_image: attachment,
      };

      const response = await api.submitLeaveRequest(leaveData);

      if (response.success) {
        setMessage({ type: 'success', text: t.successSubmit });
        
        setLeaveType('');
        setStartDate('');
        setEndDate('');
        setReason('');
        sigPad.current.clear();
        setSignatureEmpty(true);
        setAttachment(null);
        setAttachmentName('');
        setAlertMessage({ type: '', text: '' });
        
        const balanceData = await api.getLeaveBalance(currentUser.employee_id);
        setLeaveBalance(balanceData);
        fetchMyRequests(); 

      } else {
        setMessage({ type: 'error', text: response.error || t.errorSubmit });
      }
    } catch (error) {
      console.error("Error submitting leave request:", error);
      setMessage({ type: 'error', text: t.errorConnection + error.message });
    } finally {
      setSubmitting(false);
    }
  };

  const combinedLeaveUsed = leaveBalance?.used['combined_leave'];
  const quotas = leaveBalance?.current_quotas;
  
  const toggleLanguage = () => {
    const newLang = lang === 'th' ? 'en' : 'th';
    setLang(newLang);
    setMessage({ type: '', text: '' });
    setAlertMessage({ type: '', text: '' });
  };

  const formatDateByLang = (dateString) => {
    if (lang === 'th') {
      return formatDateThai(dateString);
    }
    return formatDateEng(dateString);
  };
  
  const getAttachmentInfo = () => {
      if (leaveType === 'ลาป่วย') {
          return { 
              label: t.attachmentSickLabel, 
              requiredText: totalDays >= 3 ? t.attachmentRequiredSick : ''
          };
      }
      if (leaveType === 'ลากิจ') {
          return { 
              label: t.attachmentPersonalLabel, 
              requiredText: t.attachmentRequiredPersonal 
          };
      }
      return { label: '', requiredText: '' };
  };

  const attachmentInfo = getAttachmentInfo();

  return (
    <div className="space-y-8">
      
      {/* (1) Page Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {t.title}
          </h1>
          <p className="text-gray-600 mt-2">
            {t.subtitle}
          </p>
        </div>
        <button
          onClick={toggleLanguage}
          className="flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
        >
          <Globe className="h-5 w-5 mr-2 text-gray-500" />
          {t.langToggle}
        </button>
      </div>

      {/* (2) ส่วนแสดงสรุปโควต้ารวม */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <LeaveBalanceSummary
          title={t.balanceTitleTimes}
          loading={loadingBalance}
          used={combinedLeaveUsed?.used_times ? parseInt(combinedLeaveUsed.used_times) : 0}
          max={quotas?.leave_times ?? 0}
          unit={t.balanceUnitTimes}
          loadingText={t.balanceLoading}
          remainingText={t.balanceRemaining}
          usedText={t.balanceUsed}
          ofText={t.balanceOf}
          unlimitedText={t.balanceUnlimited}
        />
        <LeaveBalanceSummary
          title={t.balanceTitleDays}
          loading={loadingBalance}
          used={combinedLeaveUsed?.used_days ? parseFloat(combinedLeaveUsed.used_days) : 0}
          max={quotas?.leave_days ?? 0}
          unit={t.balanceUnitDays}
          loadingText={t.balanceLoading}
          remainingText={t.balanceRemaining}
          usedText={t.balanceUsed}
          ofText={t.balanceOf}
          unlimitedText={t.balanceUnlimited}
        />
      </div>

      {/* (3) ฟอร์ม */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <Calendar className="h-6 w-6 mr-2 text-blue-500" />
            {t.formTitle}
          </h2>
        </div>

        
        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            
            {/* ประเภทการลา */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t.leaveTypeLabel} *
              </label>
              <select
                value={leaveType}
                onChange={(e) => setLeaveType(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={loadingBalance}
              >
                <option value="">{t.leaveTypePlaceholder}</option>
                <option value="ลาป่วย">{t.leaveTypeSick}</option>
                <option value="ลากิจ">{t.leaveTypePersonal}</option>
                <option value="ลาคลอด">{t.leaveTypeMaternity}</option>
              </select>
            </div>

            {/* วันที่ลา */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.startDateLabel} *
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.endDateLabel} *
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  min={startDate}
                />
              </div>
            </div>

            {/* สรุปจำนวนวัน */}
            {totalDays > 0 && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-md text-center">
                <span className="text-blue-800 font-medium">
                  {t.totalDaysPrefix} {formatDays(totalDays)} {t.totalDaysSuffix}
                </span>
              </div>
            )}

            {/* เหตุผลการลา */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t.reasonLabel} *
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows="4"
                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t.reasonPlaceholder}
                required
              />
            </div>

            {/* ส่วนแนบไฟล์ */}
            {(leaveType === 'ลาป่วย' || leaveType === 'ลากิจ') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {attachmentInfo.label}
                  {attachmentInfo.requiredText && 
                    <span className="text-red-500 ml-1">{attachmentInfo.requiredText}</span>
                  }
                </label>
                
                <input 
                  type="file" 
                  accept="image/*" 
                  ref={fileInputRef} 
                  onChange={handleFileChange} 
                  className="hidden"
                />

                {!attachment ? (
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current.click()}
                      disabled={submitting}
                      className="flex-1 flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                    >
                      <Paperclip className="h-5 w-5 mr-2" />
                      {t.attachmentButton}
                    </button>
                  </div>
                ) : (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-md flex items-center justify-between">
                    <div className="flex items-center truncate">
                      <CheckCircle className="h-5 w-5 mr-2 text-green-600 flex-shrink-0" />
                      <span className="text-sm text-green-800 font-medium truncate" title={attachmentName}>
                        {attachmentName}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => { setAttachment(null); setAttachmentName(''); }}
                      className="ml-2 text-red-600 hover:text-red-800 text-sm font-medium flex-shrink-0 transition-colors"
                    >
                      {t.attachmentRemoving}
                    </button>
                  </div>
                )}
                {message.type === 'error' && message.text.includes(t.errorAttachmentRequired('')) && ( 
                  <p className="mt-2 text-sm text-red-600">{message.text}</p>
                )}
              </div>
            )}

            {/* ส่วนลายเซ็น */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t.signatureLabel} *
              </label>
              <div className={`w-full border rounded-md overflow-hidden bg-gray-50 ${signatureEmpty && message.text.includes(t.errorSignature) ? 'border-red-500' : 'border-gray-300'}`}>
                <SignatureCanvas
                  ref={sigPad}
                  penColor='black'
                  backgroundColor='white'
                  canvasProps={{
                    className: 'w-full h-32 cursor-crosshair'
                  }}
                  onEnd={() => setSignatureEmpty(sigPad.current.isEmpty())}
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  sigPad.current.clear();
                  setSignatureEmpty(true);
                }}
                className="mt-2 text-sm text-red-600 hover:text-red-800 transition-colors"
              >
                {t.signatureClear}
              </button>
            </div>

            {/* ส่วนแจ้งเตือนโควต้า */}
            {alertMessage.text && (
              <div
                className={`p-4 rounded-md flex items-start border ${
                  alertMessage.type === 'error' ? 'bg-red-50 text-red-700 border-red-200' :
                  alertMessage.type === 'warning' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
                  'bg-blue-50 text-blue-700 border-blue-200'
                }`}
              >
                {alertMessage.type === 'error' || alertMessage.type === 'warning' ? (
                  <AlertCircle className="h-5 w-5 mr-3 flex-shrink-0" />
                ) : (
                  <Info className="h-5 w-5 mr-3 flex-shrink-0" />
                )}
                <span className="text-sm">{alertMessage.text}</span>
              </div>
            )}

            {/* ส่วนแสดงผลลัพธ์การยื่น */}
            {message.text && !message.text.includes(t.errorAttachmentRequired('')) && (
              <div
                className={`p-4 rounded-md flex items-center border ${
                  message.type === 'success' ? 'bg-green-50 text-green-700 border-green-200' : 
                  (message.type === 'info' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-red-50 text-red-700 border-red-200')
                }`}
              >
                {message.type === 'success' ? <CheckCircle className="h-5 w-5 mr-3 flex-shrink-0" /> :
                 message.type === 'info' ? <Info className="h-5 w-5 mr-3 flex-shrink-0" /> :
                 <AlertCircle className="h-5 w-5 mr-3 flex-shrink-0" />}
                <span className="text-sm">{message.text}</span>
              </div>
            )}

            {/* ปุ่ม Submit */}
            <div className="border-t pt-6">
              <button
                type="submit"
                disabled={submitting || loadingBalance || signatureEmpty || (alertMessage.type === 'error' && !alertMessage.text.includes(t.errorDateInvalid))}
                className={`w-full py-3 px-6 rounded-md flex items-center justify-center space-x-2 text-lg font-medium transition-colors shadow-sm
                  ${(submitting || signatureEmpty || (alertMessage.type === 'error' && !alertMessage.text.includes(t.errorDateInvalid))) ? 'bg-gray-400 cursor-not-allowed text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}
                `}
              >
                {submitting ? (
                  <>
                    <Loader2 className="animate-spin h-5 w-5" />
                    <span>{t.submitButtonSubmitting}</span>
                  </>
                ) : (
                  <>
                    <Send className="h-5 w-5" />
                    <span>{signatureEmpty ? t.submitButtonSigning : t.submitButtonText}</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* (4) ส่วนประวัติการลาของฉัน */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <List className="h-6 w-6 mr-2 text-blue-500" />
            {t.listTitle}
          </h2>
        </div>
        
        <div className="p-6">
          {loadingList && (
            <div className="flex justify-center items-center h-40">
              <Loader2 className="h-10 w-10 text-blue-500 animate-spin" />
              <span className="ml-4 text-gray-600">{t.listLoading}</span>
            </div>
          )}

          {listError && (
            <div className="p-4 rounded-md bg-red-50 text-red-700 flex items-center">
              <AlertCircle className="h-5 w-5 mr-3 flex-shrink-0" />
              <span>{listError}</span>
            </div>
          )}

          {!loadingList && !listError && (
            <div className="space-y-4">
              {myRequests.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                  <p className="text-gray-500">{t.listEmpty}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t.listHeaderType}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t.listHeaderDate}</th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{t.listHeaderDays}</th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{t.listHeaderStatus}</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t.listHeaderManage}</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {myRequests.map(req => (
                        <tr key={req.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                            {req.leave_type === 'ลาป่วย' ? t.leaveTypeSick :
                             req.leave_type === 'ลากิจ' ? t.leaveTypePersonal :
                             req.leave_type === 'ลาคลอด' ? t.leaveTypeMaternity : req.leave_type}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                            {formatDateByLang(req.start_date)} - {formatDateByLang(req.end_date)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-center">{formatDays(req.total_days)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(req.status)}`}>
                              {req.status === 'pending' ? t.listStatusPending : 
                               req.status === 'approved' ? t.listStatusApproved : t.listStatusRejected}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex justify-end space-x-2">
                              
                              {req.attachment_image && (
                                <button
                                  title={t.listAttachmentTooltip}
                                  onClick={() => handlePrintAttachment(req.attachment_image, t)}
                                  className="p-2 text-purple-600 hover:text-purple-900 hover:bg-purple-100 rounded-md transition-colors"
                                >
                                  <Paperclip className="h-5 w-5" />
                                </button>
                              )}
                              
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default LeaveRequestForm;